/**
 * L'écran des Paramètres — les listes que chaque société règle elle-même.
 *
 * Maquette `docs/maquettes/parametres.html`, validée le 04/09/2026.
 *
 * TROIS LISTES, UN SEUL ÉCRAN, parce que ce sont trois fois les mêmes gestes :
 * ajouter, renommer, désactiver, supprimer si personne ne s'en sert. Ce qui les
 * distingue tient dans un descripteur — le titre, la présence d'un abrégé, le
 * mot qui compte l'emploi — et non dans trois composants qui divergeraient au
 * premier changement porté d'un seul côté.
 *
 * CE QUE CET ÉCRAN NE FAIT PAS
 * ---------------------------------------------------------------------------
 *   - il ne règle AUCUN droit : les rôles n'existent pas encore en v2, et
 *     quiconque entre dans Stafa peut déjà créer un client ;
 *   - il ne range pas les lignes à la main : trois à six lignes rangées par nom
 *     n'ont pas besoin d'un ordre réglable, contrairement aux camions du
 *     planning, que ni la plaque ni le nom ne rangent comme l'exploitation les
 *     veut ;
 *   - il ne touche pas à ce qui est DÉJÀ ENREGISTRÉ. Renommer un support ne
 *     réécrit pas les étapes : elles gardent le mot du jour du transport, comme
 *     elles gardent l'adresse du site. Le type d'un camion, lui, est une clé —
 *     le renommer corrige tous les camions d'un coup, et c'est voulu.
 */

import { useCallback, useEffect, useState } from 'react';
import { EcranImport } from './EcranImport.js';
import { enEuros } from './ot.js';
import { OngletRattachementPennylane } from './OngletRattachementPennylane.js';
import { PanneauSourcesPennylane } from './PanneauSourcesPennylane.js';
import { OngletIndexationCarburant } from './OngletIndexationCarburant.js';
import { OngletSequenceRelance } from './OngletSequenceRelance.js';
import {
  creerLigneReglee,
  lireListeReglee,
  lireMotifsLitige,
  modifierLigneReglee,
  changerMotifLitige,
  creerMotifLitige,
  supprimerLigneReglee,
  supprimerMotifLitige,
  type EchecApi,
  type LigneReglee,
  type Resultat,
} from './api.js';
import {
  LARGEUR_DE_REFERENCE_CM,
  combienTiennent,
  metresLineaires,
  mlLisible,
  poser,
  refuse,
  type Support,
} from '@stafa/core';

/** Les listes de l'écran, dans l'ordre des onglets. */
export type CleListe =
  | 'supports'
  | 'motifs'
  | 'types'
  | 'types_document'
  | 'centres'
  | 'natures_prestation';

/**
 * LES UNITÉS D'UNE NATURE DE PRESTATION — 15/09/2026.
 *
 * LA MÊME LISTE FERMÉE QU'EN BASE et qu'à l'API (`UNITES` dans
 * `apps/api/src/parametres.ts`). Recopiée, parce qu'un menu déroulant a besoin
 * des libellés lisibles que la valeur stockée n'a pas : « unite » sans accent
 * est ce qui s'enregistre, « unité » est ce qui se lit.
 */
export const UNITES_PRESTATION: readonly {
  readonly valeur: string;
  readonly libelle: string;
}[] = [
  { valeur: 'heure', libelle: 'heure' },
  { valeur: 'jour', libelle: 'jour' },
  { valeur: 'unite', libelle: 'unité' },
  { valeur: 'forfait', libelle: 'forfait' },
  { valeur: 'kilometre', libelle: 'kilomètre' },
];

function uniteLisible(valeur: string | null | undefined): string {
  if (valeur == null) return '—';
  return UNITES_PRESTATION.find((u) => u.valeur === valeur)?.libelle ?? valeur;
}

interface Descripteur {
  readonly cle: CleListe;
  readonly onglet: string;
  readonly titre: string;
  /** Le mot du champ d'ajout : « Nom du support ». */
  readonly invite: string;
  readonly abrege: boolean;
  /**
   * Cette liste porte-t-elle des DIMENSIONS ? Les supports seuls.
   *
   * Elles ne sont PAS dans la barre d'ajout : six champs y feraient une ligne
   * illisible, et on ne connaît pas toujours les cotes au moment où l'on crée
   * un support. Elles vivent dans la boîte de modification, comme la maquette
   * `docs/maquettes/dimensions.html` le montre.
   */
  readonly dimensions: boolean;
  /**
   * Cette liste porte-t-elle le drapeau ÉCHANGEABLE ? Les supports seuls.
   *
   * Il commande tout le module Emballage : la boîte « Échange » d'une étape ne
   * propose que les supports cochés ici (`EcranOrdres.tsx` ligne 1138), et
   * l'écran Emballage ne compte que ceux-là (`apps/api/src/emballage.ts`
   * ligne 319, `where echangeable and actif`). Faux par défaut : le logiciel ne
   * devine pas lesquels des supports d'une société sont consignés.
   */
  readonly echange: boolean;
  /**
   * Cette liste porte-t-elle le drapeau JOINT À LA FACTURE ? Les types de
   * document seuls, depuis le 14/09/2026.
   *
   * Il commande ce qui part chez le CLIENT : la route
   * `POST /factures/:id/pennylane/annexes` ne dépose que les pièces dont le
   * type est coché ici. Faux par défaut, et le sens du défaut compte plus que
   * sa valeur — une pièce oubliée se rattrape, une pièce partie non.
   *
   * C'est ici, et non dans le code, que se décide quels documents partent :
   * filtrer sur le NOM du type casserait le jour où quelqu'un le renomme, et
   * les pièces cesseraient de partir sans que rien ne le dise.
   */
  readonly joindre: boolean;
  /**
   * Cette liste porte-t-elle une UNITÉ et un PRIX PAR DÉFAUT ? Les natures de
   * prestation annexe seules, depuis le 15/09/2026.
   *
   * L'unité est OBLIGATOIRE à la création — la base la refuse sans — et c'est
   * pourquoi elle est le seul champ, avec le nom, à figurer dans la barre
   * d'ajout. Le prix, lui, se pose dans la boîte : on crée souvent une nature
   * avant d'avoir arrêté son tarif.
   */
  readonly tarif: boolean;
  /**
   * Le titre de cette liste est-il FÉMININ ? « une nature », contre « un
   * support » et « un centre ».
   *
   * Un drapeau plutôt qu'un accord deviné : le décompte disait « 4 natures,
   * dont 1 désactivé », constaté au rendu le 15/09/2026. Le français d'un
   * logiciel se lit, et une faute d'accord y est une faute comme une autre.
   */
  readonly feminin: boolean;
  /** Ce que compte `emploi` : « 142 étapes », « 3 camions ». */
  readonly compte: (combien: number) => string;
  /** L'aide affichée sous la barre — une phrase, jamais deux. */
  readonly aide: string;
}

export const LISTES: readonly Descripteur[] = [
  {
    cle: 'supports',
    onglet: 'Supports',
    titre: 'support',
    invite: 'Nom du support',
    abrege: true,
    dimensions: true,
    echange: true,
    joindre: false,
    tarif: false,
    feminin: false,
    compte: (n) => `${n} étape${n > 1 ? 's' : ''}`,
    aide: 'Le nom sert à la saisie d’une étape ; l’abrégé sert au planning, où la case est étroite. Renommer ne change rien aux étapes déjà enregistrées.',
  },
  {
    cle: 'motifs',
    onglet: 'Motifs de litige',
    titre: 'motif',
    invite: 'Nom du motif',
    abrege: false,
    dimensions: false,
    echange: false,
    joindre: false,
    tarif: false,
    feminin: false,
    // DES LITIGES, plus des étapes : depuis le 07/09/2026 le litige a sa table,
    // et un motif employé deux fois de suite sur la même étape compte deux
    // fois. C'est plus juste — ce sont bien deux incidents.
    compte: (n) => `${n} litige${n > 1 ? 's' : ''}`,
    aide: 'Ils se proposent à l’ouverture d’un litige sur une étape.',
  },
  {
    cle: 'types_document',
    onglet: 'Types de document',
    titre: 'type',
    invite: 'Nom du type',
    abrege: false,
    dimensions: false,
    echange: false,
    joindre: true,
    tarif: false,
    feminin: false,
    compte: (n) => `${n} document${n > 1 ? 's' : ''}`,
    aide: 'Ils classent les pièces jointes à un ordre de transport. La case « Joint à la facture » décide de ce qui part chez le client avec la facture — ne la cochez pas sur une pièce interne.',
  },
  {
    cle: 'centres',
    onglet: 'Centres de profit',
    titre: 'centre',
    invite: 'Nom du centre',
    abrege: false,
    dimensions: false,
    echange: false,
    joindre: false,
    tarif: false,
    feminin: false,
    compte: (n) => `${n} ordre${n > 1 ? 's' : ''}`,
    // IL N'Y A PAS DE « CENTRE DE COÛT » DISTINCT, et l'aide le dit pour que
    // personne ne cherche l'autre liste : un centre est une activité, et une
    // activité porte ses recettes comme ses charges.
    aide: 'Ce sont vos activités — Marchandises Générales, Express, Bennes. Chaque ordre de transport en porte une, obligatoirement, et c’est ce qui permettra de savoir ce que chacune rapporte.',
  },
  {
    cle: 'natures_prestation',
    onglet: 'Prestations annexes',
    titre: 'nature',
    invite: 'Nom de la nature',
    abrege: false,
    dimensions: false,
    echange: false,
    joindre: false,
    tarif: true,
    feminin: true,
    compte: (n) => `${n} prestation${n > 1 ? 's' : ''}`,
    aide: 'Ce qui se facture EN PLUS du transport : attente, immobilisation, palettes non rendues. Le prix pré-remplit la saisie sur un ordre et y reste modifiable — le changer ici ne réécrit aucun ordre passé.',
  },
  {
    cle: 'types',
    onglet: 'Types de véhicule',
    titre: 'type',
    invite: 'Nom du type',
    abrege: false,
    dimensions: false,
    echange: false,
    joindre: false,
    tarif: false,
    feminin: false,
    compte: (n) => `${n} camion${n > 1 ? 's' : ''}`,
    aide: 'Le type d’un camion pointe vers cette liste : le renommer corrige tous les camions qui le portent.',
  },
];

function descripteur(cle: CleListe): Descripteur {
  return LISTES.find((l) => l.cle === cle) ?? (LISTES[0] as Descripteur);
}

/**
 * Le chemin d'API d'un onglet. Les motifs de litige n'en ont pas ici : ils
 * existaient avant cet écran, sous `/motifs-litige`, et gardent leurs routes.
 */
function listeDe(
  cle: CleListe,
): 'supports' | 'types' | 'types_document' | 'centres' | 'natures_prestation' {
  if (cle === 'supports') return 'supports';
  if (cle === 'types_document') return 'types_document';
  if (cle === 'centres') return 'centres';
  if (cle === 'natures_prestation') return 'natures_prestation';
  return 'types';
}

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly lignes: readonly LigneReglee[] }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette ligne n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris dans cette liste.',
  invalide: 'Le nom est obligatoire, et l’abrégé ne dépasse pas douze caractères.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache:
    'Des fiches s’y rattachent encore : désactivez cette ligne plutôt que de la supprimer.',
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee:
    'Une étape déjà validée ne se déplace plus, et ne perd plus son camion.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

/** Le renommage en cours, s'il y en a un. */
interface Renommage {
  readonly id: string;
  readonly nom: string;
  readonly abrege: string;
  /** En CENTIMÈTRES, et en CHAÎNE : un champ vide vaut « pas mesuré ». */
  readonly longueur: string;
  readonly largeur: string;
  readonly hauteur: string;
  readonly etages: string;
  /** L'unité et le tarif d'une nature de prestation. En CHAÎNE, vide = absent. */
  readonly unite: string;
  readonly prix: string;
  readonly emploi: number;
}

/** Les champs de la boîte, pour ne pas les énumérer à chaque appel. */
type ChampBoite =
  | 'nom'
  | 'abrege'
  | 'longueur'
  | 'largeur'
  | 'hauteur'
  | 'etages'
  | 'unite'
  | 'prix';

/**
 * Un champ de la boîte, en nombre pour l'API. Le vide devient `null` — c'est
 * ainsi qu'on RETIRE une dimension saisie à tort.
 *
 * Rend `undefined` si ce n'est pas un nombre : l'API refuserait, mais autant ne
 * pas lui envoyer. L'écran laisse alors la valeur inchangée plutôt que de
 * l'effacer en silence.
 */
function retenirSiConnu(
  cle: string,
  valeur: number | null | undefined,
): Record<string, number | null> {
  return valeur === undefined ? {} : { [cle]: valeur };
}

function nombreSaisi(valeur: string): number | null | undefined {
  const propre = valeur.trim();
  if (propre === '') return null;
  if (!/^[0-9]+$/.test(propre)) return undefined;
  return Number(propre);
}

// ---------------------------------------------------------------------------
// Les dimensions, mises en mots
// ---------------------------------------------------------------------------

/**
 * Une ligne réglée vue comme un support, pour `chargement.ts`.
 *
 * Les listes sans dimensions n'ont pas ces colonnes : `undefined` y devient
 * `null`, et le calcul répondra « dimensions absentes » — ce qui est vrai.
 */
function commeSupport(ligne: LigneReglee): Support {
  return {
    nom: ligne.nom,
    abrege: ligne.abrege ?? null,
    longueur_cm: ligne.longueur_cm ?? null,
    largeur_cm: ligne.largeur_cm ?? null,
    hauteur_cm: ligne.hauteur_cm ?? null,
    etages: ligne.etages ?? 1,
  };
}

function mlDe(ligne: LigneReglee): number | null {
  return metresLineaires(commeSupport(ligne));
}

/** « 120 × 80 × 110 », ou ce qui manque. */
function dimensionsDites(ligne: LigneReglee): string {
  const l = ligne.longueur_cm ?? null;
  const g = ligne.largeur_cm ?? null;
  const h = ligne.hauteur_cm ?? null;
  const etages = ligne.etages ?? 1;
  // Les étages tiennent dans cette colonne plutôt que dans la leur : ils
  // décrivent le même support, et une colonne de plus faisait déborder le
  // tableau de 114 px en 1280 — mesuré le 04/09/2026. Ils ne s'écrivent que
  // s'il y en a plus d'un : « ×1 » sur chaque ligne serait du bruit.
  const gerbage = etages > 1 ? ` · ${etages} étages` : '';
  if (l === null && g === null && h === null) {
    return etages > 1 ? `à renseigner${gerbage}` : 'à renseigner';
  }
  const dit = (v: number | null) => (v === null ? '?' : String(v));
  return `${dit(l)} × ${dit(g)} × ${dit(h)}${gerbage}`;
}

/**
 * Pourquoi cette ligne n'a pas de mètre linéaire, EN FRANÇAIS.
 *
 * Affiché en infobulle sur la cellule. Un tiret seul laisserait croire à une
 * panne ; ici on dit ce qui manque, et l'exploitation sait quoi faire.
 */
function pourquoiPasDeMl(ligne: LigneReglee): string | null {
  const r = poser(commeSupport(ligne));
  if (!refuse(r)) return null;
  if (r.refus === 'dimensions_absentes') {
    return 'Longueur et largeur manquantes : aucun mètre linéaire ne se calcule, et ce support n’apparaîtra dans aucune jauge de remplissage.';
  }
  if (r.refus === 'trop_large') {
    return `Ce support dépasse ${LARGEUR_DE_REFERENCE_CM} cm dans les deux sens : il ne se pose sur aucune largeur de référence, et aucun mètre linéaire ne se calcule.`;
  }
  return 'Le nombre d’étages doit valoir au moins 1.';
}

/** Ce que la boîte affiche pendant qu'on tape — le résultat, avant d'enregistrer. */
function apercuDuCalcul(renommage: Renommage): string {
  const nombre = (v: string) => {
    const propre = v.trim();
    return propre === '' || !/^[0-9]+$/.test(propre) ? null : Number(propre);
  };
  const etages = nombre(renommage.etages) ?? 1;
  const r = poser({
    nom: renommage.nom,
    abrege: renommage.abrege,
    longueur_cm: nombre(renommage.longueur),
    largeur_cm: nombre(renommage.largeur),
    hauteur_cm: nombre(renommage.hauteur),
    etages,
  });

  if (refuse(r)) {
    if (r.refus === 'dimensions_absentes') {
      return 'Sans longueur ni largeur, aucun remplissage ne se calculera pour ce support.';
    }
    if (r.refus === 'trop_large') {
      return `Plus large que ${LARGEUR_DE_REFERENCE_CM} cm dans les deux sens : rien ne se calculera.`;
    }
    return 'Le nombre d’étages doit valoir au moins 1.';
  }

  // 13,20 ML : la capacité d'une semi. Écrite ici pour donner un repère
  // parlant, et non pour dire quoi que ce soit d'une remorque en particulier.
  const dansUneSemi = combienTiennent(
    {
      nom: renommage.nom,
      abrege: renommage.abrege,
      longueur_cm: nombre(renommage.longueur),
      largeur_cm: nombre(renommage.largeur),
      hauteur_cm: nombre(renommage.hauteur),
      etages,
    },
    13.2,
  );

  return (
    `Sur ${LARGEUR_DE_REFERENCE_CM} cm de large : ${r.pose.deFront} de front, ` +
    `${r.pose.etages} étage${r.pose.etages > 1 ? 's' : ''}, ` +
    `${mlLisible(r.pose.ml)} ML l’unité` +
    (dansUneSemi === null ? '' : ` — soit ${dansUneSemi} dans une semi de 13,20 ML.`)
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

/**
 * LES ONGLETS DE L'ÉCRAN. Les quatre listes réglées, plus l'IMPORT.
 *
 * SORTIS DE `VueParametres` le 07/09/2026, quand l'import a ouvert. La barre
 * doit surmonter DEUX corps différents — un tableau de liste ou l'écran
 * d'import —, et la laisser à l'intérieur de l'un des deux aurait obligé à la
 * recopier dans l'autre. Deux barres jumelles divergent au premier changement
 * porté d'un seul côté ; c'est exactement ce qui était arrivé à la barre haute
 * le 03/09.
 *
 * L'IMPORT EST UN ONGLET ET NON UN SOUS-MENU. Le module Paramètres n'a
 * volontairement aucun sous-menu (voir `Application.tsx`) : ses écrans se
 * lisent d'affilée, et une barre de plus au-dessus d'un tableau de quatre
 * lignes ferait deux niveaux pour rien.
 */
/**
 * `pennylane` ajouté le 11/09/2026 : le rattachement des clients à Pennylane.
 *
 * IL EST UN ONGLET SPÉCIAL, comme l'import, et non une « liste réglée ». Les
 * cinq premiers onglets partagent un même composant générique — un tableau
 * nom/abrégé. Le rattachement n'a pas cette forme : deux fichiers mis en
 * regard, une recherche par ligne, et rien à créer.
 */
/**
 * `gazole` ajouté le 15/09/2026 : la publication mensuelle du gazole.
 *
 * TROISIÈME ONGLET SPÉCIAL, pour la même raison que les deux autres : les
 * listes réglées partagent un moule `id` / `nom` / `actif` / société, et
 * `indices_gazole` n'a rien de tout ça — sa clé est le MOIS, et elle
 * n'appartient à aucune société.
 */
export type OngletParametres = CleListe | 'import' | 'pennylane' | 'gazole' | 'relances';

export function OngletsParametres({
  onglet,
  quandOnglet,
}: {
  readonly onglet: OngletParametres;
  readonly quandOnglet: (choisi: OngletParametres) => void;
}) {
  return (
    <div className="onglets-listes">
      {LISTES.map((liste) => (
        <button
          key={liste.cle}
          type="button"
          className={liste.cle === onglet ? 'onglet-liste actif' : 'onglet-liste'}
          onClick={() => quandOnglet(liste.cle)}
        >
          {liste.onglet}
        </button>
      ))}
      <button
        type="button"
        className={onglet === 'import' ? 'onglet-liste actif' : 'onglet-liste'}
        onClick={() => quandOnglet('import')}
      >
        Import
      </button>
      <button
        type="button"
        className={onglet === 'pennylane' ? 'onglet-liste actif' : 'onglet-liste'}
        onClick={() => quandOnglet('pennylane')}
      >
        Pennylane
      </button>
      <button
        type="button"
        className={onglet === 'gazole' ? 'onglet-liste actif' : 'onglet-liste'}
        onClick={() => quandOnglet('gazole')}
      >
        Indexation carburant
      </button>
      <button
        type="button"
        className={onglet === 'relances' ? 'onglet-liste actif' : 'onglet-liste'}
        onClick={() => quandOnglet('relances')}
      >
        Relances
      </button>
    </div>
  );
}

export interface ProprietesVueParametres {
  readonly cle: CleListe;
  readonly etat: Etat;
  readonly nouveau: {
    readonly nom: string;
    readonly abrege: string;
    readonly unite: string;
  };
  readonly renommage: Renommage | null;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandNouveau: (champ: 'nom' | 'abrege' | 'unite', valeur: string) => void;
  readonly quandAjouter: () => void;
  readonly quandRenommer: (ligne: LigneReglee) => void;
  readonly quandChampRenommage: (champ: ChampBoite, valeur: string) => void;
  readonly quandEnregistrerRenommage: () => void;
  readonly quandFermerRenommage: () => void;
  readonly quandBasculerActif: (ligne: LigneReglee) => void;
  readonly quandBasculerEchange: (ligne: LigneReglee) => void;
  readonly quandBasculerJoindre: (ligne: LigneReglee) => void;
  readonly quandSupprimer: (ligne: LigneReglee) => void;
}

export function VueParametres({
  cle,
  etat,
  nouveau,
  renommage,
  refus,
  envoi,
  quandNouveau,
  quandAjouter,
  quandRenommer,
  quandChampRenommage,
  quandEnregistrerRenommage,
  quandFermerRenommage,
  quandBasculerActif,
  quandBasculerEchange,
  quandBasculerJoindre,
  quandSupprimer,
}: ProprietesVueParametres) {
  const dit = descripteur(cle);
  const lignes = etat.nom === 'prete' ? etat.lignes : [];
  const eteintes = lignes.filter((l) => !l.actif).length;

  return (
    <div className="parametres">
      <form
        className="barre-liste-reglee"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          quandAjouter();
        }}
      >
        <span className="decompte">
          {etat.nom === 'chargement'
            ? 'Chargement…'
            : etat.nom === 'panne'
              ? 'Stafa ne répond pas.'
              : `${lignes.length} ${dit.titre}${lignes.length > 1 ? 's' : ''}${
                  eteintes > 0
                    ? `, dont ${eteintes} désactivé${dit.feminin ? 'e' : ''}${
                        eteintes > 1 ? 's' : ''
                      }`
                    : ''
                }`}
        </span>
        <input
          className="champ"
          type="text"
          value={nouveau.nom}
          placeholder={dit.invite}
          disabled={envoi}
          onChange={(evenement) => quandNouveau('nom', evenement.target.value)}
        />
        {dit.abrege && (
          <input
            className="champ court"
            type="text"
            value={nouveau.abrege}
            placeholder="Abrégé"
            disabled={envoi}
            onChange={(evenement) => quandNouveau('abrege', evenement.target.value)}
          />
        )}
        {/* L'UNITÉ EST DANS LA BARRE, contrairement au prix : la base la
            refuse à la création, et sans défaut — « heure » paraîtrait le
            choix évident, et une nature au forfait créée sans y penser se
            facturerait à l'heure. */}
        {dit.tarif && (
          <select
            className="champ court"
            aria-label="Unité"
            value={nouveau.unite}
            disabled={envoi}
            onChange={(evenement) => quandNouveau('unite', evenement.target.value)}
          >
            {UNITES_PRESTATION.map((u) => (
              <option key={u.valeur} value={u.valeur}>
                {u.libelle}
              </option>
            ))}
          </select>
        )}
        <button className="bouton" type="submit" disabled={envoi}>
          Ajouter
        </button>
      </form>

      <p className="aide-liste">{dit.aide}</p>
      {refus === null ? null : <p className="bandeau-refus">{refus}</p>}

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              {dit.abrege && <th>Abrégé</th>}
              {dit.dimensions && <th className="col-large">Dimensions (cm)</th>}
              {dit.dimensions && <th>ML / unité</th>}
              {dit.echange && <th>Échangeable</th>}
              {dit.joindre && <th>Joint à la facture</th>}
              {dit.tarif && <th>Unité</th>}
              {dit.tarif && <th>Prix par défaut HT</th>}
              <th>Emploi</th>
              <th>État</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 && etat.nom === 'prete' && (
              <tr>
                <td
                  colSpan={
                    (dit.abrege ? 5 : 4) +
                    (dit.dimensions ? 2 : 0) +
                    (dit.echange ? 1 : 0) +
                    (dit.joindre ? 1 : 0) +
                    (dit.tarif ? 2 : 0)
                  }
                  className="rien"
                >
                  Aucun {dit.titre} pour l’instant. Ajoutez le premier ci-dessus.
                </td>
              </tr>
            )}
            {lignes.map((ligne) => (
              <tr key={ligne.id} className={ligne.actif ? undefined : 'eteinte'}>
                <td>{ligne.nom}</td>
                {dit.abrege && (
                  <td className="code">
                    {ligne.abrege === null || ligne.abrege === undefined
                      ? '—'
                      : ligne.abrege}
                  </td>
                )}
                {dit.dimensions && (
                  <td className="code col-large">{dimensionsDites(ligne)}</td>
                )}
                {dit.dimensions && (
                  <td className="code" title={pourquoiPasDeMl(ligne) ?? undefined}>
                    {mlLisible(mlDe(ligne))}
                  </td>
                )}
                {/* UNE CASE, PAS UN BOUTON « Rendre échangeable » : c'est un
                    état à lire d'un coup d'œil sur toute la colonne, et
                    l'exploitation en coche plusieurs d'affilée. Elle écrit
                    immédiatement — il n'y a rien à valider. */}
                {dit.echange && (
                  <td>
                    <label className="case-echangeable">
                      <input
                        type="checkbox"
                        checked={ligne.echangeable === true}
                        disabled={envoi}
                        onChange={() => quandBasculerEchange(ligne)}
                      />
                      <span className="sourdine">
                        {ligne.echangeable === true ? 'Consigné' : 'Perdu'}
                      </span>
                    </label>
                  </td>
                )}
                {/* CE QUI PART CHEZ LE CLIENT, coché ici et nulle part
                    ailleurs. Même geste que la case « Échangeable » : elle
                    écrit immédiatement, il n'y a rien à valider.

                    LE MOT SOUS LA CASE DIT LA CONSÉQUENCE, pas l'état :
                    « Coché » n'apprendrait rien à qui regarde la case. */}
                {dit.joindre && (
                  <td>
                    <label className="case-echangeable">
                      <input
                        type="checkbox"
                        checked={ligne.joindre_facture === true}
                        disabled={envoi}
                        onChange={() => quandBasculerJoindre(ligne)}
                      />
                      <span className="sourdine">
                        {ligne.joindre_facture === true
                          ? 'Part avec la facture'
                          : 'Reste chez nous'}
                      </span>
                    </label>
                  </td>
                )}
                {dit.tarif && <td>{uniteLisible(ligne.unite)}</td>}
                {/* LE PRIX EST UN REPLI, PAS UNE RÈGLE : une nature sans tarif
                    existe — un service chiffré au cas par cas. Le tiret le dit,
                    là où « 0,00 » voudrait dire « gratuit ». */}
                {dit.tarif && (
                  <td className="code">
                    {ligne.prix_defaut == null
                      ? '—'
                      : (enEuros(ligne.prix_defaut) ?? '—')}
                  </td>
                )}
                <td className="sourdine">
                  {ligne.emploi === 0 ? 'jamais employé' : dit.compte(ligne.emploi)}
                </td>
                <td>
                  <span className={ligne.actif ? 'etat-liste' : 'etat-liste eteint'}>
                    {ligne.actif ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td className="actions-liste">
                  <button
                    className="lien-action"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandRenommer(ligne)}
                  >
                    {/* « Modifier » dès qu'il y a autre chose qu'un nom à
                        changer : le bouton doit dire ce que la boîte fait, et
                        elle porte quatre champs de plus sur les supports.
                        Constaté en REGARDANT la page rendue le 04/09/2026. */}
                    {dit.dimensions || dit.tarif ? 'Modifier' : 'Renommer'}
                  </button>
                  <button
                    className="lien-action"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandBasculerActif(ligne)}
                  >
                    {ligne.actif ? 'Désactiver' : 'Réactiver'}
                  </button>
                  {ligne.emploi === 0 && (
                    <button
                      className="lien-action risque"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandSupprimer(ligne)}
                    >
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renommage === null ? null : (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>
              {dit.dimensions ? 'Modifier' : 'Renommer'} un {dit.titre}
            </h3>
            <p className="quoi-decroche">
              {renommage.emploi === 0
                ? 'Jamais employé pour l’instant.'
                : cle === 'types'
                  ? `Porté par ${dit.compte(renommage.emploi)}, qui suivront le nouveau nom.`
                  : `Employé par ${dit.compte(renommage.emploi)} déjà enregistrées, qui ne changeront pas.`}
            </p>
            <form
              onSubmit={(evenement) => {
                evenement.preventDefault();
                quandEnregistrerRenommage();
              }}
            >
              <div className={dit.abrege ? 'deux-boites' : undefined}>
                <div>
                  <label className="etiquette-boite" htmlFor="renommage-nom">
                    Nom
                  </label>
                  <input
                    id="renommage-nom"
                    className="champ-boite"
                    type="text"
                    value={renommage.nom}
                    disabled={envoi}
                    onChange={(evenement) =>
                      quandChampRenommage('nom', evenement.target.value)
                    }
                  />
                </div>
                {dit.abrege && (
                  <div>
                    <label className="etiquette-boite" htmlFor="renommage-abrege">
                      Abrégé
                    </label>
                    <input
                      id="renommage-abrege"
                      className="champ-boite"
                      type="text"
                      value={renommage.abrege}
                      disabled={envoi}
                      onChange={(evenement) =>
                        quandChampRenommage('abrege', evenement.target.value)
                      }
                    />
                  </div>
                )}
              </div>
              {dit.dimensions && (
                <>
                  <div className="trois-boites">
                    {(
                      [
                        ['longueur', 'Longueur'],
                        ['largeur', 'Largeur'],
                        ['hauteur', 'Hauteur'],
                      ] as const
                    ).map(([champ, etiquette]) => (
                      <div key={champ}>
                        <label
                          className="etiquette-boite"
                          htmlFor={`renommage-${champ}`}
                        >
                          {etiquette}
                        </label>
                        <input
                          id={`renommage-${champ}`}
                          className="champ-boite"
                          type="text"
                          inputMode="numeric"
                          value={renommage[champ]}
                          disabled={envoi}
                          onChange={(evenement) =>
                            quandChampRenommage(champ, evenement.target.value)
                          }
                        />
                        <p className="unite-boite">cm</p>
                      </div>
                    ))}
                  </div>
                  <div className="deux-boites">
                    <div>
                      <label className="etiquette-boite" htmlFor="renommage-etages">
                        Étages empilables
                      </label>
                      <input
                        id="renommage-etages"
                        className="champ-boite"
                        type="text"
                        inputMode="numeric"
                        value={renommage.etages}
                        disabled={envoi}
                        onChange={(evenement) =>
                          quandChampRenommage('etages', evenement.target.value)
                        }
                      />
                      <p className="unite-boite">
                        1 = on ne gerbe pas. C’est vous qui savez, pas le logiciel.
                      </p>
                    </div>
                    <div />
                  </div>
                  <p className="apercu-boite">{apercuDuCalcul(renommage)}</p>
                </>
              )}
              {dit.tarif && (
                <>
                  <label className="etiquette-boite" htmlFor="renommage-unite">
                    Unité
                  </label>
                  <select
                    id="renommage-unite"
                    className="champ-boite"
                    value={renommage.unite}
                    disabled={envoi}
                    onChange={(evenement) =>
                      quandChampRenommage('unite', evenement.target.value)
                    }
                  >
                    {UNITES_PRESTATION.map((u) => (
                      <option key={u.valeur} value={u.valeur}>
                        {u.libelle}
                      </option>
                    ))}
                  </select>
                  <label className="etiquette-boite" htmlFor="renommage-prix">
                    Prix par défaut HT
                  </label>
                  <input
                    id="renommage-prix"
                    className="champ-boite"
                    type="text"
                    inputMode="decimal"
                    value={renommage.prix}
                    disabled={envoi}
                    onChange={(evenement) =>
                      quandChampRenommage('prix', evenement.target.value)
                    }
                  />
                  {/* CE QUE LE CHANGEMENT NE FAIT PAS, dit ici plutôt que
                      découvert : le prix est RECOPIÉ dans chaque prestation au
                      moment où on la saisit. */}
                  <p className="unite-boite">
                    Il pré-remplit la saisie sur un ordre et y reste modifiable.
                    Le changer ne réécrit aucune prestation déjà enregistrée.
                    Laissez vide si cette nature se chiffre au cas par cas.
                  </p>
                </>
              )}
              {refus !== null && <p className="faute-boite">{refus}</p>}
              <div className="pied-boite">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={quandFermerRenommage}
                >
                  Annuler
                </button>
                <button className="bouton" type="submit" disabled={envoi}>
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesParametres {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /**
   * Ouvrir l'ecran DIRECTEMENT sur l'onglet Import, demande par l'accueil le
   * 08/09/2026 : son action « + Import de donnees » deposerait sinon la
   * personne sur la liste des supports.
   *
   * Lu A LA PREMIERE CONSTRUCTION SEULEMENT, et c'est suffisant : l'ecran est
   * demonte des qu'on quitte le module, donc chaque entree dans Parametres le
   * relit. En faire un etat suivi permettrait de rouvrir l'import sous les
   * doigts de quelqu'un qui vient de le fermer.
   */
  readonly ouvrirImport?: boolean;
}

export function EcranParametres({
  jeton,
  quandAccesPerdu,
  ouvrirImport = false,
}: ProprietesParametres) {
  const [cle, setCle] = useState<CleListe>('supports');
  /**
   * L'onglet IMPORT est un drapeau A COTE de la liste courante, et non une
   * cinquieme valeur de `cle`.
   *
   * La raison est concrete : `cle` commande le chargement des lignes reglees.
   * Y ajouter « import » obligerait chaque lecture a s'en garder, et revenir
   * de l'import rechargerait une liste qu'on n'a pas quittee. Ainsi, aller a
   * l'import et revenir retrouve l'onglet ou l'on etait, sans un appel.
   */
  /**
   * L'ONGLET SPÉCIAL COURANT, ou `null` quand on est sur une liste réglée.
   *
   * UN SEUL ÉTAT, et non un booléen par onglet — changé le 11/09/2026 en
   * ajoutant Pennylane. Deux drapeaux exclusifs finissent toujours par être
   * vrais en même temps le jour où l'on oublie d'en éteindre un, et l'écran
   * afficherait alors deux corps superposés.
   */
  const [special, setSpecial] = useState<'import' | 'pennylane' | 'gazole' | 'relances' | null>(
    ouvrirImport ? 'import' : null,
  );
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [nouveau, setNouveau] = useState({ nom: '', abrege: '', unite: 'heure' });
  const [renommage, setRenommage] = useState<Renommage | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Les motifs de litige ne passent pas par les mêmes routes que les deux
   * autres listes : ils existaient avant cet écran, sous `/motifs-litige`. On
   * les ramène ici à la MÊME FORME plutôt que de dessiner un troisième tableau.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    setEtat({ nom: 'chargement' });

    const lu: Resultat<readonly LigneReglee[]> =
      cle === 'motifs'
        ? await (async () => {
            const motifs = await lireMotifsLitige(j);
            return motifs.etat === 'ok'
              ? ({
                  etat: 'ok',
                  valeur: motifs.valeur.map((m) => ({
                    id: m.id,
                    nom: m.nom,
                    actif: m.actif,
                    emploi: m.emploi ?? 0,
                  })),
                } as const)
              : motifs;
          })()
        : await lireListeReglee(j, listeDe(cle));

    if (lu.etat !== 'ok') {
      if (lu.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
      return;
    }
    setEtat({ nom: 'prete', lignes: lu.valeur });
  }, [avecJeton, cle, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    apres?: () => void,
  ) => {
    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        setEnvoi(false);
        if (apres !== undefined) apres();
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(REFUS[resultat.cause]);
      setEnvoi(false);
    })();
  };

  const listeApi = listeDe(cle);

  return (
    <main className="corps corps-parametres">
      <OngletsParametres
        onglet={special ?? cle}
        quandOnglet={(choisi) => {
          if (choisi === 'import' || choisi === 'pennylane' || choisi === 'gazole' || choisi === 'relances') {
            setSpecial(choisi);
            // Le refus de la liste précédente ne concerne pas cet onglet : le
            // laisser afficherait un message sans rapport avec ce qu'on voit.
            setRefus(null);
            return;
          }
          setSpecial(null);
          setCle(choisi);
          setNouveau({ nom: '', abrege: '', unite: 'heure' });
          setRenommage(null);
          setRefus(null);
        }}
      />
      {special === 'import' ? (
        <EcranImport jeton={jeton} quandAccesPerdu={quandAccesPerdu} />
      ) : special === 'pennylane' ? (
        <>
          <OngletRattachementPennylane
            jeton={jeton}
            quandAccesPerdu={quandAccesPerdu}
            messageRefus={(cause) => REFUS[cause]}
          />
          {/* LES SOURCES DE L'ANALYSE, lot 96 (18/09/2026) : les droits du
              jeton et les copies lues, sous le rattachement — c'est le même
              jeton, et c'est ici qu'on vient quand Pennylane ne répond pas. */}
          <PanneauSourcesPennylane
            jeton={jeton}
            quandAccesPerdu={quandAccesPerdu}
            messageRefus={(cause) => REFUS[cause]}
          />
        </>
      ) : special === 'relances' ? (
        // LOT 160 : la séquence des relances, demandée au cadrage du 03/11.
        <OngletSequenceRelance
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          messageRefus={(cause) => REFUS[cause]}
        />
      ) : special === 'gazole' ? (
        <OngletIndexationCarburant
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          messageRefus={(cause) => REFUS[cause]}
        />
      ) : (
        <VueParametres
          cle={cle}
          etat={etat}
          nouveau={nouveau}
          renommage={renommage}
          refus={refus}
          envoi={envoi}
          quandNouveau={(champ, valeur) =>
            setNouveau((actuel) => ({ ...actuel, [champ]: valeur }))
          }
          quandAjouter={() => {
            if (nouveau.nom.trim() === '') {
              setRefus('Le nom est obligatoire.');
              return;
            }
            const nom = nouveau.nom.trim();
            const abrege = nouveau.abrege.trim();
            ecrire(
              (j) =>
                cle === 'motifs'
                  ? creerMotifLitige(j, nom)
                  : creerLigneReglee(j, listeApi, {
                      nom,
                      ...(cle === 'supports' && abrege !== '' ? { abrege } : {}),
                      // L'UNITÉ N'EST ENVOYÉE QUE PAR LA LISTE QUI LA PORTE :
                      // l'API refuse un champ inconnu sur les autres.
                      ...(cle === 'natures_prestation'
                        ? { unite: nouveau.unite }
                        : {}),
                    }),
              () =>
                setNouveau({ nom: '', abrege: '', unite: nouveau.unite }),
            );
          }}
          quandRenommer={(ligne) =>
            setRenommage({
              id: ligne.id,
              nom: ligne.nom,
              abrege: ligne.abrege ?? '',
              // En CHAÎNE, et vide quand la valeur est absente : le champ doit
              // pouvoir rester vide, et un « 0 » affiché d'office serait une
              // dimension inventée.
              longueur:
                ligne.longueur_cm === null || ligne.longueur_cm === undefined
                  ? ''
                  : String(ligne.longueur_cm),
              largeur:
                ligne.largeur_cm === null || ligne.largeur_cm === undefined
                  ? ''
                  : String(ligne.largeur_cm),
              hauteur:
                ligne.hauteur_cm === null || ligne.hauteur_cm === undefined
                  ? ''
                  : String(ligne.hauteur_cm),
              etages: String(ligne.etages ?? 1),
              unite: ligne.unite ?? 'heure',
              prix: ligne.prix_defaut ?? '',
              emploi: ligne.emploi,
            })
          }
          quandChampRenommage={(champ, valeur) =>
            setRenommage((actuel) =>
              actuel === null ? null : { ...actuel, [champ]: valeur },
            )
          }
          quandEnregistrerRenommage={() => {
            if (renommage === null) return;
            const nom = renommage.nom.trim();
            if (nom === '') {
              setRefus('Le nom est obligatoire.');
              return;
            }
            const abrege = renommage.abrege.trim();

            // Une saisie qui n'est pas un nombre n'est PAS envoyée : `undefined`
            // laisse la valeur inchangée, là où `null` l'effacerait. Taper « abc »
            // dans la longueur ne doit pas supprimer la longueur enregistrée.
            const dimensions = descripteur(cle).dimensions
              ? {
                  ...retenirSiConnu('longueur_cm', nombreSaisi(renommage.longueur)),
                  ...retenirSiConnu('largeur_cm', nombreSaisi(renommage.largeur)),
                  ...retenirSiConnu('hauteur_cm', nombreSaisi(renommage.hauteur)),
                  // `etages` est `not null` en base : un champ vide le ramène à 1
                  // plutôt que de faire échouer l'écriture.
                  etages: nombreSaisi(renommage.etages) ?? 1,
                }
              : {};

            ecrire(
              (j) =>
                cle === 'motifs'
                  ? changerMotifLitige(j, renommage.id, { nom })
                  : modifierLigneReglee(j, listeApi, renommage.id, {
                      nom,
                      ...(cle === 'supports'
                        ? { abrege: abrege === '' ? null : abrege }
                        : {}),
                      ...dimensions,
                      // LE PRIX VIDE S'ENVOIE À `null`, et c'est ainsi qu'on
                      // RETIRE un tarif posé à tort — même mécanique que les
                      // dimensions d'un support.
                      ...(cle === 'natures_prestation'
                        ? {
                            unite: renommage.unite,
                            prix_defaut:
                              renommage.prix.trim() === ''
                                ? null
                                : renommage.prix.trim().replace(',', '.'),
                          }
                        : {}),
                    }),
              () => setRenommage(null),
            );
          }}
          quandFermerRenommage={() => {
            setRenommage(null);
            setRefus(null);
          }}
          quandBasculerActif={(ligne) =>
            ecrire((j) =>
              cle === 'motifs'
                ? changerMotifLitige(j, ligne.id, { actif: !ligne.actif })
                : modifierLigneReglee(j, listeApi, ligne.id, { actif: !ligne.actif }),
            )
          }
          /* Seuls les supports la portent : l'API ne lit `echangeable` que si la
           liste le déclare (`apps/api/src/parametres.ts` ligne 252). La case
           n'est de toute façon dessinée que là. */
          quandBasculerEchange={(ligne) =>
            ecrire((j) =>
              modifierLigneReglee(j, listeApi, ligne.id, {
                echangeable: !(ligne.echangeable === true),
              }),
            )
          }
          /* Seuls les types de document la portent : l'API ne lit
             `joindre_facture` que si la liste le déclare (descripteur
             `LISTE_TYPES_DOCUMENT` d'`apps/api/src/parametres.ts`). La case
             n'est de toute façon dessinée que là. */
          quandBasculerJoindre={(ligne) =>
            ecrire((j) =>
              modifierLigneReglee(j, listeApi, ligne.id, {
                joindre_facture: !(ligne.joindre_facture === true),
              }),
            )
          }
          quandSupprimer={(ligne) =>
            ecrire((j) =>
              cle === 'motifs'
                ? supprimerMotifLitige(j, ligne.id)
                : supprimerLigneReglee(j, listeApi, ligne.id),
            )
          }
        />
      )}
    </main>
  );
}
